import { Row } from 'antd';
import { getConfig } from 'config';
import { FormApi, FormState } from 'final-form';
import { useEffect, useState } from 'react';
import { Field as FieldRFF, AnyObject } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { useTranslation } from 'react-i18next';
import { AiFillPlusCircle } from 'react-icons/ai';

import { DocumentModel } from 'api/documents/documents.model';
import Button from 'components/Buttons';
import DocumentForm from 'components/DocumentForm';
import { DocumentFormProps } from 'components/DocumentForm/DocumentForm';
import Form from 'components/Form';
import TextArea from 'components/Form/TextArea';
import Upload from 'components/Form/Upload';
import SectionTitle from 'components/SectionTitle';
import { CertificateDetailsModel } from 'models/certificate.model';
import { ComponentDetailsModel } from 'models/certificate.model';
import { COMPANY } from 'models/company.model';

import ComponentInformation from './ComponentInformation';
import * as Styled from './NewCertPage.styles';
import { CertificateTypeConfig } from './hooks/useCategoryConfig';

type NewCertFormProps = {
  config: CertificateTypeConfig;
  handleSubmit: () => Promise<AnyObject | undefined> | undefined;
  form: FormApi<CertificateDetailsModel>;
  values: CertificateDetailsModel;
  initialValues: any;
  publish: (formState: FormState<CertificateDetailsModel>, isDraft: boolean) => Promise<void>;
  isLoading: boolean;
} & Omit<DocumentFormProps, 'formFields'>;

const createLinkedComponentFromCertificate = ({
  additionalDetails = {} as CertificateDetailsModel,
  documentBlockchainAddress,
  senderPublisherId,
}: DocumentModel<CertificateDetailsModel>): ComponentDetailsModel => {
  const supplierName =
    Object.values(COMPANY)
      .map(getConfig)
      .find(({ publisherId }) => publisherId === senderPublisherId)?.name ?? '';

  return {
    componentId: additionalDetails.productId,
    componentPassportId: documentBlockchainAddress,
    componentName: additionalDetails.productName,
    batchNumber: additionalDetails.batchNumber,
    countryOfOrigin: additionalDetails.countryOfOrigin,
    region: additionalDetails.region,
    factory: additionalDetails.factory,
    supplierId: senderPublisherId ?? '',
    supplierName,
    percentRecycled: additionalDetails.percentRecycled,
    percentShare: 0,
  };
};

const NewCertForm = ({
  config,
  handleSubmit,
  form,
  values,
  initialValues,
  publish,
  submitError,
  clearSubmitError,
  isLoading,
}: NewCertFormProps) => {
  const { t } = useTranslation();
  const [isFormPercentSharedValid, setIsFormPercentSharedValid] = useState(false);

  const validatePercentShare = (certificate: CertificateDetailsModel) => {
    const percentShareSum = certificate?.components?.reduce(
      (acc: number, item: ComponentDetailsModel) => {
        return acc + Number(item.percentShare);
      },
      0
    );
    certificate.components && certificate.components.length !== 0
      ? setIsFormPercentSharedValid(percentShareSum === 100)
      : setIsFormPercentSharedValid(true);
  };

  useEffect(() => {
    validatePercentShare(values);
  }, [values]);

  const handlePublish = (isDraft: boolean) => () => publish(form.getState(), isDraft);

  const { isDefault, uploadPDFLabel, formFields } = config;

  return (
    <Form onSubmit={handleSubmit} data-cy="form">
      <DocumentForm
        formFields={formFields}
        submitError={submitError}
        clearSubmitError={clearSubmitError}
      />
      {uploadPDFLabel && (
        <FieldRFF
          name="additionalCertificate"
          label={t(`certificate.${uploadPDFLabel}.label`)}
          data-cy="form-additionalCertificate"
          required
          onUploadFile={form.mutators.setFile}
          component={Upload}
          accept="application/pdf"
        />
      )}

      {isDefault && (
        <>
          <FieldArray name="components">
            {({ fields }) => (
              <>
                {fields.map((field, index) => (
                  <ComponentInformation
                    key={field}
                    componentIndex={index}
                    onLinkCertificate={(certificate) =>
                      form.mutators.linkComponent(
                        createLinkedComponentFromCertificate(certificate),
                        index
                      )
                    }
                    onDelete={() => fields.remove(index)}
                  />
                ))}
                <Button
                  type="text"
                  $vertical
                  icon={<AiFillPlusCircle />}
                  onClick={() => fields.push(initialValues)}
                  style={{ margin: 'auto' }}
                >
                  {t('certificate.addComponent')}
                </Button>
              </>
            )}
          </FieldArray>

          <SectionTitle>{t('certificate.calculations')}</SectionTitle>
          <Row justify="space-between" style={{ width: '100%' }}>
            <Styled.Overall>{t('certificate.overall')}</Styled.Overall>
            <Styled.Overall>{values.percentRecycled}%</Styled.Overall>
          </Row>

          <FieldRFF
            name="additionalComments"
            label={t(`certificate.additionalComments.label`)}
            placeholder={t(`certificate.additionalComments.placeholder`)}
            data-cy="form_additionalComments"
            required={false}
            component={TextArea}
            width="100%"
            rows={6}
          />
        </>
      )}

      <Row justify="space-between">
        <Button type="link" $secondaryLink onClick={() => form.reset()}>
          {t(`certificate.clearAllFields`)}
        </Button>
        <div style={{ display: 'flex' }}>
          <Styled.Button
            disabled={!isFormPercentSharedValid}
            loading={isLoading}
            htmlType="button"
            onClick={handlePublish(true)}
          >
            {t(`certificate.saveAsDraft`)}
          </Styled.Button>
          <Styled.Button
            type="primary"
            htmlType="submit"
            data-cy="form_submit"
            disabled={!isFormPercentSharedValid}
            loading={isLoading}
            onClick={handlePublish(false)}
          >
            {t(`certificate.publish`)}
          </Styled.Button>
        </div>
      </Row>
      {!isFormPercentSharedValid && (
        <Styled.ValidationMessage>{t(`certificate.percentShareNotValid`)}</Styled.ValidationMessage>
      )}
    </Form>
  );
};

export default NewCertForm;
